import { useMemo, useRef, useState } from 'react'
import { Badge, StatusBadge } from '../components/Badge'
import { Icon } from '../components/Icon'
import { PageHeader } from '../components/PageHeader'
import { useUnsavedChangesGuard } from '../components/DraftGuard'
import { categoryOptions, chargeCodeOptions, initialExpenses, projectOptions } from '../data'
import type { Expense } from '../types'

interface ExpenseDraft {
  amount: string
  category: string
  chargeCode: string
  date: string
  merchant: string
  project: string
  purpose: string
}

const blankExpense: ExpenseDraft = {
  amount: '',
  category: 'Meals',
  chargeCode: chargeCodeOptions[0],
  date: '2026-09-09',
  merchant: '',
  project: projectOptions[0],
  purpose: ''
}

const money = (amount: number, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)

export const ExpensesPage = () => {
  const [expenses, setExpenses] = useState<Expense[]>(initialExpenses)
  const [filter, setFilter] = useState<'All' | 'Action needed' | 'Submitted' | 'Approved'>('All')
  const [isFormOpen, setFormOpen] = useState(false)
  const [draft, setDraft] = useState<ExpenseDraft>(blankExpense)
  const [receipt, setReceipt] = useState<string | null>(null)
  const [isUploading, setUploading] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [isDirty, setDirty] = useState(false)
  const uploadRef = useRef<HTMLInputElement>(null)
  useUnsavedChangesGuard(isDirty)

  const shownExpenses = useMemo(() => {
    if (filter === 'Action needed') return expenses.filter((expense) => expense.status === 'returned' || expense.analysis === 'analysis_failed')
    if (filter === 'Submitted') return expenses.filter((expense) => expense.status === 'submitted')
    if (filter === 'Approved') return expenses.filter((expense) => expense.status === 'approved')
    return expenses
  }, [expenses, filter])
  const monthTotal = expenses.filter((expense) => expense.transactionDate.startsWith('2026-09')).reduce((sum, expense) => sum + expense.amount, 0)

  const updateDraft = (field: keyof ExpenseDraft, value: string) => {
    setDraft((current) => ({ ...current, [field]: value }))
    setDirty(true)
  }

  const selectReceipt = (file?: File) => {
    if (!file) return
    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(file.type) || file.size > 10 * 1024 * 1024) {
      setNotice('Receipts must be PDF, JPEG, or PNG and no larger than 10 MB.')
      return
    }
    setReceipt(file.name)
    setUploading(true)
    setDirty(true)
    window.setTimeout(() => setUploading(false), 750)
  }

  const saveExpense = () => {
    const amount = Number(draft.amount)
    if (!draft.merchant || !draft.date || !draft.category || !draft.project || !draft.chargeCode || !draft.purpose || !Number.isFinite(amount) || amount <= 0) {
      setNotice('Add a merchant, date, category, project, charge code, business purpose, and valid amount before saving.')
      return
    }
    const created: Expense = {
      id: `exp-${Date.now()}`,
      merchant: draft.merchant,
      transactionDate: draft.date,
      amount,
      currency: 'USD',
      category: draft.category,
      project: draft.project,
      chargeCode: draft.chargeCode.split(' · ')[0],
      status: 'draft',
      analysis: receipt ? 'analysis_pending' : 'analysis_failed',
      receiptName: receipt || undefined,
      flag: receipt ? undefined : 'Receipt required before submission'
    }
    setExpenses((current) => [created, ...current])
    setFormOpen(false)
    setDraft(blankExpense)
    setReceipt(null)
    setDirty(true)
    setNotice('Expense saved as a draft. It remains editable until submission.')
  }

  const submitExpense = (id: string) => {
    setExpenses((current) => current.map((expense) => expense.id === id ? { ...expense, status: 'submitted', analysis: expense.receiptName ? 'analysis_pending' : 'analysis_failed' } : expense))
    setDirty(false)
    setNotice('Expense submitted. Receipt analysis will run in the background and any signals will be shown to your manager.')
  }

  return (
    <div className="page expenses-page">
      <PageHeader
        eyebrow="EXPENSES"
        title="Spend with a clear paper trail."
        description="Secure receipts, explainable checks, and human review—without slowing you down."
        actions={<button className="button button-primary" onClick={() => { setFormOpen(true); setNotice(null) }}><Icon name="plus" size={17} /> Add expense</button>}
      />

      {notice && <div className="inline-notice" role="status"><Icon name="check" size={17} /><span>{notice}</span><button onClick={() => setNotice(null)} aria-label="Dismiss"><Icon name="close" size={16} /></button></div>}

      <section className="expense-insights">
        <article><span className="insight-icon"><Icon name="credit-card" size={19} /></span><div><span>September submitted</span><strong>{money(monthTotal)}</strong></div><small>3 expenses</small></article>
        <article><span className="insight-icon insight-icon-warning"><Icon name="warning" size={19} /></span><div><span>Needs attention</span><strong>1</strong></div><small>Returned for details</small></article>
        <article><span className="insight-icon insight-icon-mint"><Icon name="sparkle" size={19} /></span><div><span>Receipt analysis</span><strong>2 complete</strong></div><small>1 pending review</small></article>
        <div className="expense-secure-note"><Icon name="shield" size={17} /><span>Receipts are encrypted and only accessible to authorized reviewers.</span></div>
      </section>

      <section className="panel expenses-panel">
        <div className="expenses-panel-toolbar">
          <div className="filter-tabs" role="tablist" aria-label="Expense status filter">
            {(['All', 'Action needed', 'Submitted', 'Approved'] as const).map((item) => <button key={item} role="tab" aria-selected={filter === item} className={filter === item ? 'active' : ''} onClick={() => setFilter(item)}>{item}{item === 'Action needed' && <span>1</span>}</button>)}
          </div>
          <button className="button button-secondary button-small"><Icon name="download" size={16} /> Export</button>
        </div>
        <div className="expense-list">
          {shownExpenses.map((expense) => (
            <article className="expense-row" key={expense.id}>
              <span className={`expense-category-icon ${expense.status === 'returned' ? 'warning' : ''}`}><Icon name={expense.category === 'Lodging' ? 'calendar' : expense.category === 'Meals' ? 'receipt' : 'credit-card'} size={19} /></span>
              <div className="expense-primary"><div className="expense-title-line"><h3>{expense.merchant}</h3><StatusBadge status={expense.status} /></div><p>{expense.category} · {expense.project} · {new Date(`${expense.transactionDate}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>{expense.flag && <span className="expense-flag"><Icon name="warning" size={14} /> {expense.flag}</span>}</div>
              <div className="expense-receipt"><Icon name="file" size={16} /><span>{expense.receiptName || 'No receipt yet'}</span></div>
              <div className="expense-analysis"><StatusBadge status={expense.analysis} /></div>
              <div className="expense-amount"><strong>{money(expense.amount, expense.currency)}</strong><small>{expense.currency}</small></div>
              <div className="expense-actions"><button className="icon-button" aria-label={`Open ${expense.merchant}`} onClick={() => setNotice(`${expense.merchant} details are ready for the connected expense API.`)}><Icon name="chevron-right" size={18} /></button>{expense.status === 'draft' && <button className="button button-dark button-small" onClick={() => submitExpense(expense.id)}>Submit</button>}</div>
            </article>
          ))}
          {!shownExpenses.length && <div className="empty-state"><Icon name="receipt" size={26} /><h3>No matching expenses</h3><p>Try another status filter or capture a new expense.</p></div>}
        </div>
      </section>

      <section className="receipt-process-card">
        <div className="receipt-process-copy"><p className="eyebrow">HOW RECEIPT REVIEW WORKS</p><h2>Helpful signals. Your manager makes the decision.</h2><p>After a receipt uploads directly to private storage, QTE extracts the visible fields and compares them to your entry. Low confidence and policy signals stay visible for a human reviewer.</p></div>
        <div className="process-steps"><span><i>1</i><strong>Secure upload</strong></span><span><i>2</i><strong>Analysis</strong></span><span><i>3</i><strong>Human decision</strong></span></div>
      </section>

      {isFormOpen && (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setFormOpen(false)}>
          <section className="modal-panel expense-modal" role="dialog" aria-modal="true" aria-labelledby="expense-heading" onMouseDown={(event) => event.stopPropagation()}>
            <header className="modal-header"><div><p className="eyebrow">NEW EXPENSE</p><h2 id="expense-heading">Capture the details</h2></div><button className="icon-button" onClick={() => setFormOpen(false)} aria-label="Close"><Icon name="close" /></button></header>
            <div className="form-grid expense-form-grid">
              <label className="form-span-2">Merchant<input value={draft.merchant} placeholder="e.g. Northwind Hotel" onChange={(event) => updateDraft('merchant', event.target.value)} /></label>
              <label>Transaction date<input type="date" value={draft.date} onChange={(event) => updateDraft('date', event.target.value)} /></label>
              <label>Amount (USD)<input type="number" min="0" step="0.01" value={draft.amount} placeholder="0.00" onChange={(event) => updateDraft('amount', event.target.value)} /></label>
              <label>Category<select value={draft.category} onChange={(event) => updateDraft('category', event.target.value)}>{categoryOptions.map((category) => <option key={category}>{category}</option>)}</select></label>
              <label>Project<select value={draft.project} onChange={(event) => updateDraft('project', event.target.value)}>{projectOptions.map((project) => <option key={project}>{project}</option>)}</select></label>
              <label>Charge code<select value={draft.chargeCode} onChange={(event) => updateDraft('chargeCode', event.target.value)}>{chargeCodeOptions.map((code) => <option key={code}>{code}</option>)}</select></label>
              <label className="form-span-2">Business purpose<textarea rows={3} value={draft.purpose} placeholder="Who was this for, and why was it needed?" onChange={(event) => updateDraft('purpose', event.target.value)} /></label>
              <div className="form-span-2 receipt-upload-section"><div><strong>Receipt</strong><span>PDF, JPEG, or PNG; 10 MB and five pages maximum. Malware scanning is required before manager access.</span></div><input ref={uploadRef} className="visually-hidden" type="file" accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png" onChange={(event) => selectReceipt(event.target.files?.[0])} /><button className={`receipt-dropzone ${receipt ? 'has-file' : ''}`} onClick={() => uploadRef.current?.click()} type="button"><span className="receipt-drop-icon"><Icon name={receipt ? 'check' : 'upload'} size={20} /></span><span><strong>{isUploading ? 'Uploading securely…' : receipt || 'Upload receipt'}</strong><small>{receipt ? 'Scoped upload ready for API connection' : 'PDF, JPG, or PNG · 10 MB maximum'}</small></span><Icon name="chevron-right" size={18} /></button></div>
            </div>
            <footer className="modal-footer"><button className="button button-secondary" onClick={() => setFormOpen(false)}>Cancel</button><button className="button button-primary" onClick={saveExpense} disabled={isUploading}>Save expense <Icon name="arrow-right" size={17} /></button></footer>
          </section>
        </div>
      )}
    </div>
  )
}
